import {
  type CustomerId,
  type DeviceId,
  type VMSCameraId,
  type VMSStreamId,
} from '@solinkcorp/api-types';
import { type ThumbnailLevel } from 'components/VideoPlayer/components/PlayerThumbnail/VideoThumbnail';
import { type CLOUD_DOWNLOAD_MP4, CLOUD_DOWNLOAD_ZIP, type PushToCloudStatus } from 'vd-constants';
import { type IEvent } from '../platform/platform';
import { type AbstractRequestor } from '../shared/AbstractRequestor';
import { normalizeEvent } from '../shared/eventUtils';

interface IStatusParams {
  deviceId: string; // The device UUID
  cameraId: string; // The camera ID associated with the device (i.e. not the camera UUID)
  streamId: string; // The stream UUID
  eventId: string; // The event UUID
  start: string | number; // Video clip start time (epoch timestamp)
  end: string | number; // Video clip end time (epoch timestamp)
}

interface DownloadParams {
  eventId: string;
  cameraId: string;
  tenantId: string;
  streamId: string;
}

export interface VideoBackupStreamsStorage {
  customerId: CustomerId;
  deviceId: DeviceId;
  cameraId: VMSCameraId;
  streamId: VMSStreamId;
  // The number of days dailyAverageBytes is averaged over. This does not
  // need to be used in combination with dailyAverageBytes.
  dailyAverageSampledDays: number;
  dailyAverageBytes: number;
  totalBytes: number;
  // The number of days actually stored in the cloud
  retainedDays: number;
  level: ThumbnailLevel;
}

export type CloudDownloadType = typeof CLOUD_DOWNLOAD_MP4 | typeof CLOUD_DOWNLOAD_ZIP;

interface CheckDownloadUrlParams extends DownloadParams {
  type: CloudDownloadType;
}

export class CloudVmsApi {
  constructor(
    private readonly requestor: AbstractRequestor,
    private readonly cloudFrontHost: string,
  ) {}

  // Get the status for the video clip upload.
  getPushToCloudStatus(tenantId: string, p: IStatusParams) {
    return this.requestor.get<{
      state: PushToCloudStatus;
      pushedSegments: number;
      totalSegments: number;
    }>({
      url: `/tenants/${tenantId}/devices/${p.deviceId}/cameras/${p.cameraId}/streams/${p.streamId}/shares/${p.eventId}`,
      params: { start: p.start, end: p.end },
    });
  }

  async updateSharedEvent(params: { eventId: string }, eventData: unknown) {
    const updatedEvent = (await this.requestor.put<unknown>({
      url: `/shares/${params.eventId}`,
      data: eventData,
    })) as IEvent;
    return normalizeEvent(updatedEvent);
  }

  // Get the shared single page application link for specific share
  getSharedViewLink(params: { eventId: string; shareId: string }): Promise<{ url: string }> {
    return this.requestor.get({
      url: `/shares/${params.eventId}/id/${params.shareId}/token`,
    });
  }

  // Get all the shared single page application links for specific event
  getAllSharedViewLinks(eventId: string) {
    return this.requestor.get<unknown>({ url: `/shares/${eventId}/token` });
  }

  // Generate the mp4 download url of shared video clip
  getMp4DownloadUrl({ tenantId, eventId, cameraId, streamId }: DownloadParams) {
    return `${this.cloudFrontHost}/${tenantId}/shares/${eventId}/videos/${cameraId}/${streamId}/download.mp4`;
  }

  // Generate the zip download url of shared video clip
  getZipDownloadUrl({ tenantId, eventId, cameraId, streamId }: DownloadParams) {
    return `${this.cloudFrontHost}/${tenantId}/shares/${eventId}/videos/${cameraId}/${streamId}/download.win.zip`;
  }

  // Check the download url of shared video clip
  // It can be url for mp4 file or zip file
  checkDownloadUrl({ tenantId, eventId, cameraId, streamId, type }: CheckDownloadUrlParams) {
    const url =
      type === CLOUD_DOWNLOAD_ZIP
        ? this.getZipDownloadUrl({ tenantId, eventId, cameraId, streamId })
        : this.getMp4DownloadUrl({ tenantId, eventId, cameraId, streamId });
    return this.requestor.request({
      method: 'HEAD',
      url,
      options: { withCredentials: true },
    });
  }

  /** Share video that's already in cloud.
   *  This support sharing video from multiple cameras at once. */
  startCloudVideoShare(params: {
    tenantId: string;
    deviceId: DeviceId;
    eventId: string;
    payload: Record<string, unknown>[];
  }) {
    const { tenantId, deviceId, eventId, payload } = params;
    return this.requestor.post({
      url: `/tenants/${tenantId}/devices/${deviceId}/shares/${eventId}`,
      data: payload,
    });
  }

  getVideoBackupStreamsStorage() {
    return this.requestor.get<VideoBackupStreamsStorage[]>({
      url: `/cloud-storage-metric/usage`,
    });
  }
}
